import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Tooltip } from './Tooltip';

/**
 * Radix Tabs, so arrow-key roving focus and the aria wiring come for free.
 * The supplier screen (INV-12/13/14) and the patient file both live in tabs and
 * both fill the viewport, so the content region — not the page — is what
 * scrolls (DS-01).
 *
 * NFR-05 inverts that below 1024px: the strip sticks, the panel flows, and the
 * page is the scroller. See `mobileScroll` for the one case that opts back out.
 */

export interface TabSpec {
  key: string;
  /** Arabic label. */
  label: string;
  icon?: LucideIcon;
  /** Rendered as a small chip after the label; `0` still shows. */
  count?: number;
  /**
   * Drop the label and render the icon alone, with the `label` as a tooltip and
   * `aria-label`. `count` rides a small badge on the icon's corner. For narrow
   * docks where the label buys nothing (the visit sidebar).
   */
  iconOnly?: boolean;
  disabled?: boolean;
  /**
   * Keep the panel mounted while another tab is showing, for content that holds
   * work not yet saved (the visit's procedure cards).
   */
  keepMounted?: boolean;
  content: ReactNode;
}

type RootProps = ComponentPropsWithoutRef<typeof TabsPrimitive.Root>;

export interface TabsProps
  extends Omit<RootProps, 'value' | 'onValueChange' | 'onChange' | 'children'> {
  tabs: TabSpec[];
  value: string;
  onChange: (value: string) => void;
  variant?: 'underline' | 'pill';
  /** Toolbar pinned to the end of the tab bar — search, buttons, a live count. */
  actions?: ReactNode;
  listClassName?: string;
  contentClassName?: string;
  /**
   * Stretch the tabs across the whole strip — each one takes an equal share
   * instead of hugging its content and leaving a gap at the end. For a fixed
   * dock where a row of icon tabs would otherwise bunch up on one side.
   */
  fill?: boolean;
  /** Let a table or a nested panel sit flush inside the content region. */
  noPadding?: boolean;
  /**
   * NFR-05 — what scrolls below 1024px.
   *
   * `page` (the default): the panel stops being its own scroll region, the tab
   * strip sticks to the top of whatever is scrolling, and the content flows on
   * into it. A nested scroller inside a 375px column is the thing that makes a
   * phone feel wrong — two bars, a dead zone at the edge of the inner one, and
   * a header that scrolls away anyway because the outer one moved.
   *
   * `contain`: keep DS-01's inner scroll. For the surfaces that are already the
   * whole viewport and have no page behind them to scroll — the calendar's side
   * panel, which is a full-screen overlay on a phone.
   */
  mobileScroll?: 'page' | 'contain';
}

/**
 * Radix defaults its roving focus to LTR. The document direction follows the
 * user's language (NFR-03), so read it rather than hard-coding either one.
 */
function documentDir(): 'rtl' | 'ltr' {
  if (typeof document === 'undefined') return 'rtl';
  return document.documentElement.dir === 'ltr' ? 'ltr' : 'rtl';
}

const TRIGGER_BASE =
  'group inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-bold transition ' +
  'disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none';

// NFR-05 — a 28px tab is a mouse target. The `max-lg:` half lifts it to DS-54's
// 44px, which is also what makes the strip comfortable to flick sideways.
const TRIGGER_VARIANT = {
  underline:
    '-mb-px border-b-2 border-transparent px-3 py-2 text-slate-600 hover:text-slate-800 ' +
    'data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 ' +
    'max-lg:min-h-touch max-lg:px-3.5 max-lg:text-sm',
  pill:
    'rounded border border-transparent px-2.5 py-1 text-slate-600 hover:bg-slate-200/60 ' +
    'data-[state=active]:border-blue-200 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 ' +
    'max-lg:min-h-[2.25rem] max-lg:px-3 max-lg:text-sm',
} as const;

const CHIP_VARIANT = {
  underline: 'bg-slate-100 text-slate-600 group-data-[state=active]:bg-blue-100 group-data-[state=active]:text-blue-700',
  pill: 'bg-slate-200 text-slate-600 group-data-[state=active]:bg-blue-100 group-data-[state=active]:text-blue-700',
} as const;

export const Tabs = forwardRef<ElementRef<typeof TabsPrimitive.Root>, TabsProps>(
  (
    {
      tabs,
      value,
      onChange,
      variant = 'underline',
      actions,
      listClassName,
      contentClassName,
      noPadding,
      fill = false,
      mobileScroll = 'page',
      className,
      dir,
      ...props
    },
    ref,
  ) => (
    <TabsPrimitive.Root
      ref={ref}
      value={value}
      onValueChange={onChange}
      dir={dir ?? documentDir()}
      className={cn(
        'flex h-full min-h-0 flex-col',
        // The height the desktop hands this component is a ceiling. Release it
        // on a phone or the panel below can only ever scroll inside it.
        mobileScroll === 'page' && 'max-lg:h-auto',
        className,
      )}
      {...props}
    >
      {/* NFR-05 — the actions drop under the strip on a phone rather than
          fighting it for the same row: `ms-auto` on a full bar would squeeze the
          tabs to nothing before the buttons gave up any width. */}
      <div
        className={cn(
          'flex shrink-0 items-center gap-2 max-lg:flex-wrap',
          variant === 'underline' && 'border-b border-slate-200 bg-white px-2',
          // The strip is what tells the user which tab they are in, so it is the
          // one part that stays. `bg-white` unconditionally: the pill variant
          // has no background of its own, and a transparent sticky bar shows the
          // rows travelling underneath it. z-30 puts it over anything the panel
          // stacks against its own top edge — a table header, a pinned toolbar.
          mobileScroll === 'page' && 'max-lg:sticky max-lg:top-0 max-lg:z-30 max-lg:bg-white',
        )}
      >
        <TabsPrimitive.List
          className={cn(
            'touch-scroll touch-scroll-x flex max-w-full items-center overflow-x-auto overflow-y-hidden',
            variant === 'underline' ? 'gap-1' : 'gap-0.5 rounded bg-slate-100 p-0.5',
            fill && 'w-full',
            listClassName,
          )}
        >
          {tabs.map((tab) => (
            <TabsPrimitive.Trigger
              key={tab.key}
              value={tab.key}
              data-testid={`tab-${tab.key}`}
              disabled={tab.disabled}
              // The label is gone visually, so keep it as the accessible name.
              aria-label={tab.iconOnly ? tab.label : undefined}
              className={cn(
                TRIGGER_BASE,
                TRIGGER_VARIANT[variant],
                // `flex-1` (basis 0 + grow) splits the strip evenly; the base's
                // `shrink-0` only bites when a label is wider than its share.
                fill && 'flex-1 justify-center',
                // Icon-only: the trigger drops its own padding; the span below
                // fills the tab so hovering anywhere on it opens the tooltip.
                // `mb-0` cancels the variant's `-mb-px`, whose protruding pixel
                // the List's `overflow-x-auto` would clip back to a hairline.
                tab.iconOnly && 'p-0 mb-0',
              )}
            >
              {tab.iconOnly ? (
                // The tooltip hangs off this inner span, never off the trigger:
                // Radix's Tooltip Trigger merges its open/closed `data-state`
                // onto its child, and Tabs.Trigger spreads incoming props after
                // its own `data-state`, so wrapping the trigger would clobber
                // the active-tab styling.
                <Tooltip content={tab.label}>
                  <span className="flex w-full items-center justify-center px-2.5 py-2">
                    <span className="relative inline-flex">
                      {tab.icon ? <tab.icon className="h-5 w-5" /> : null}
                      {tab.count !== undefined && tab.count > 0 ? (
                        // The label is gone, so the count rides the icon's
                        // corner as a badge — the tab stays icon-sized. A zero
                        // badge is noise, so only a live count shows.
                        <span
                          className={cn(
                            'absolute -end-1.5 -top-1 ltr-num rounded-full px-1 py-px',
                            'font-mono text-[9px] font-bold',
                            CHIP_VARIANT[variant],
                          )}
                        >
                          {tab.count}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </Tooltip>
              ) : (
                <>
                  {tab.icon ? <tab.icon className="h-4 w-4" /> : null}
                  {tab.label}
                  {tab.count !== undefined ? (
                    // DS-11/DS-51 — the count is a number, so mono and LTR.
                    <span
                      className={cn(
                        'ltr-num rounded-full px-1.5 py-px font-mono text-[10px] font-bold',
                        CHIP_VARIANT[variant],
                      )}
                    >
                      {tab.count}
                    </span>
                  ) : null}
                </>
              )}
            </TabsPrimitive.Trigger>
          ))}
        </TabsPrimitive.List>
        {actions ? (
          <div className="ms-auto flex shrink-0 items-center gap-1.5 max-lg:ms-0 max-lg:w-full max-lg:flex-wrap max-lg:justify-end max-lg:pb-1.5">
            {actions}
          </div>
        ) : null}
      </div>

      {tabs.map((tab) => (
        <TabsPrimitive.Content
          key={tab.key}
          value={tab.key}
          forceMount={tab.keepMounted || undefined}
          data-testid={`tab-panel-${tab.key}`}
          className={cn(
            'touch-scroll min-h-0 flex-1 overflow-auto focus-visible:outline-none',
            tab.keepMounted && 'data-[state=inactive]:hidden',
            mobileScroll === 'page' && 'max-lg:flex-none max-lg:overflow-visible',
            !noPadding && 'p-3 max-lg:p-2',
            contentClassName,
          )}
        >
          {tab.content}
        </TabsPrimitive.Content>
      ))}
    </TabsPrimitive.Root>
  ),
);
Tabs.displayName = 'Tabs';
